import { useRef } from 'react';
import type { CommunicationPatientRef } from '../../../../services/communications';
import { PatientChip } from './PatientChip';
import { PatientPicker } from './PatientPicker';
import { RestrictedPatientPicker } from './RestrictedPatientPicker';

export const ALREADY_NOTED_LABEL = 'Note already created';

interface AddPatientNotePatientsFieldProps {
    labelId: string;
    selectedPatients: CommunicationPatientRef[];
    alreadyNotedPatients: CommunicationPatientRef[];
    alreadyNotedIds: Set<string>;
    /** The thread's actual linked patients - not `selectedPatients` (this
     *  session's picks) - is what the pickers offer to add from. */
    linkedPatients: CommunicationPatientRef[];
    /** Null on a practice-less thread - restricts the picker to `linkedPatients` only. */
    practiceId: string | null;
    isSaving: boolean;
    pickerOpen: boolean;
    onTogglePicker: () => void;
    onClosePicker: () => void;
    onPick: (patient: CommunicationPatientRef) => void;
    onRemove: (patientId: string) => void;
}

/** The modal's "Patients" field: selected + already-noted chips, and
 *  whichever picker variant fits (practice search, or a restricted list of
 *  the thread's own linked patients when there's no practice). */
export function AddPatientNotePatientsField({
    labelId,
    selectedPatients,
    alreadyNotedPatients,
    alreadyNotedIds,
    linkedPatients,
    practiceId,
    isSaving,
    pickerOpen,
    onTogglePicker,
    onClosePicker,
    onPick,
    onRemove
}: AddPatientNotePatientsFieldProps) {
    const addPatientRef = useRef<HTMLButtonElement>(null);

    const addablePatients = linkedPatients.filter(
        (p) => !selectedPatients.some((sp) => sp.id === p.id)
    );
    const canOfferMorePatients = practiceId !== null || addablePatients.length > 0;

    return (
        <div role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
            {selectedPatients.length === 0 && alreadyNotedPatients.length === 0 ? (
                <p className="text-[12px] text-gray-400">No patients selected</p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {selectedPatients.map((p) => (
                        <PatientChip key={p.id} name={p.name} onRemove={() => onRemove(p.id)} />
                    ))}
                    {alreadyNotedPatients.map((p) => (
                        <PatientChip
                            key={p.id}
                            name={p.name}
                            disabled
                            title={ALREADY_NOTED_LABEL}
                        />
                    ))}
                </div>
            )}
            {canOfferMorePatients && (
                <button
                    ref={addPatientRef}
                    type="button"
                    disabled={isSaving}
                    onClick={onTogglePicker}
                    className="self-start h-8 px-3 rounded-lg border border-dashed border-gray-300 hover:border-gray-800 flex items-center gap-1.5 text-[12px] font-semibold text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    + Add patient
                </button>
            )}
            {pickerOpen && practiceId && (
                <PatientPicker
                    triggerRef={addPatientRef}
                    practiceId={practiceId}
                    linkedIds={selectedPatients.map((p) => p.id)}
                    disabledIds={[...alreadyNotedIds]}
                    disabledLabel={ALREADY_NOTED_LABEL}
                    onPick={onPick}
                    onClose={onClosePicker}
                />
            )}
            {pickerOpen && !practiceId && (
                <RestrictedPatientPicker
                    triggerRef={addPatientRef}
                    options={addablePatients}
                    alreadyNotedIds={alreadyNotedIds}
                    onPick={onPick}
                    onClose={onClosePicker}
                />
            )}
        </div>
    );
}
